import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Home, LogOut, Calendar, PoundSterling, Eye, Clock,
  Loader2, MapPin, CheckCircle, AlertCircle, Search,
  MessageSquare, Building2, User
} from "lucide-react";
import { API } from "@/App";

const getBuyerAuth = () => {
  const token = localStorage.getItem("buyer_token");
  const applicant = JSON.parse(localStorage.getItem("buyer_applicant") || "{}");
  return { token, applicant };
};

export const BuyerDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const { token, applicant } = getBuyerAuth();
  
  // Feedback modal
  const [feedbackModal, setFeedbackModal] = useState(false);
  const [selectedViewing, setSelectedViewing] = useState(null);
  const [feedbackAction, setFeedbackAction] = useState("");
  const [feedbackNotes, setFeedbackNotes] = useState("");
  const [offerAmount, setOfferAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      navigate("/buyer/login");
      return;
    }
    fetchDashboard();
  }, [token]);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/buyer-portal/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboard(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem("buyer_token");
        localStorage.removeItem("buyer_applicant");
        navigate("/buyer/login");
      }
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("buyer_token");
    localStorage.removeItem("buyer_applicant");
    navigate("/buyer/login");
  };

  const openFeedbackModal = (viewing) => {
    setSelectedViewing(viewing);
    setFeedbackAction("");
    setFeedbackNotes("");
    setOfferAmount("");
    setFeedbackModal(true);
  };

  const submitFeedback = async () => {
    if (!feedbackAction) {
      toast.error("Please select an action");
      return;
    }
    
    if (feedbackAction === "offer" && !offerAmount) {
      toast.error("Please enter an offer amount");
      return;
    }
    
    setSubmitting(true);
    try {
      const payload = {
        viewing_id: selectedViewing.viewing_id,
        interested: feedbackAction !== "not_interested",
        action: feedbackAction,
        notes: feedbackNotes,
        offer_amount: feedbackAction === "offer" ? parseFloat(offerAmount) : null
      };
      
      await axios.post(
        `${API}/buyer-portal/viewings/${selectedViewing.viewing_id}/feedback`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(
        feedbackAction === "offer" 
          ? "Offer submitted successfully!" 
          : feedbackAction === "second_viewing"
          ? "Second viewing requested!"
          : "Thank you for your feedback"
      );
      
      setFeedbackModal(false);
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit feedback");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <Home className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-semibold text-slate-800">Buyer Portal</h1>
                <p className="text-sm text-slate-500">
                  Welcome, {dashboard?.applicant?.first_name}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={() => navigate("/buyer/properties")}>
                <Search className="w-4 h-4 mr-2" />
                Search Properties
              </Button>
              <Button variant="ghost" onClick={handleLogout}>
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Accepted Offer Banner - Prompt to complete onboarding */}
        {dashboard?.accepted_offers?.length > 0 && (
          <Card className="mb-6 bg-gradient-to-r from-green-600 to-green-700 text-white border-0">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold flex items-center gap-2">
                    <CheckCircle className="w-6 h-6" />
                    Congratulations! Your offer has been accepted
                  </h2>
                  <p className="text-green-100 mt-1">
                    Complete your buyer onboarding to proceed with the purchase
                  </p>
                </div>
                <Button
                  onClick={() => navigate("/buyer/onboarding")}
                  className="bg-white text-green-700 hover:bg-green-50"
                >
                  Complete Onboarding
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500">Total Viewings</p>
                  <p className="text-2xl font-bold">{dashboard?.stats?.total_viewings || 0}</p>
                </div>
                <Eye className="w-8 h-8 text-blue-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500">Upcoming</p>
                  <p className="text-2xl font-bold">{dashboard?.stats?.upcoming_viewings || 0}</p>
                </div>
                <Calendar className="w-8 h-8 text-green-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500">Active Offers</p>
                  <p className="text-2xl font-bold">{dashboard?.stats?.active_offers || 0}</p>
                </div>
                <PoundSterling className="w-8 h-8 text-orange-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500">Accepted</p>
                  <p className="text-2xl font-bold text-green-600">{dashboard?.stats?.accepted_offers || 0}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Needs Feedback */}
          {dashboard?.needs_feedback?.length > 0 && (
            <Card className="lg:col-span-2 border-orange-200 bg-orange-50">
              <CardHeader>
                <CardTitle className="text-orange-700 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5" />
                  Feedback Required
                </CardTitle>
                <CardDescription>
                  Please tell us what you thought of these viewings
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {dashboard.needs_feedback.map((viewing) => (
                    <div
                      key={viewing.viewing_id}
                      className="flex items-center justify-between p-3 bg-white rounded-lg border"
                    >
                      <div>
                        <p className="font-medium">{viewing.property_address}</p>
                        <p className="text-sm text-slate-500">
                          Viewed on {new Date(viewing.requested_date).toLocaleDateString()}
                        </p>
                      </div>
                      <Button onClick={() => openFeedbackModal(viewing)}>
                        Give Feedback
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Upcoming Viewings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Upcoming Viewings
              </CardTitle>
            </CardHeader>
            <CardContent>
              {dashboard?.upcoming_viewings?.length > 0 ? (
                <div className="space-y-3">
                  {dashboard.upcoming_viewings.map((viewing) => (
                    <div
                      key={viewing.viewing_id}
                      className="p-3 bg-slate-50 rounded-lg"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium">{viewing.property_address}</p>
                          <p className="text-sm text-slate-500 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {viewing.requested_date} at {viewing.requested_time}
                          </p>
                        </div>
                        <Badge variant={viewing.status === "confirmed" ? "default" : "secondary"}>
                          {viewing.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Calendar className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No upcoming viewings</p>
                  <Button
                    variant="outline"
                    className="mt-3"
                    onClick={() => navigate("/buyer/properties")}
                  >
                    Search Properties
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Active Offers */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <PoundSterling className="w-5 h-5" />
                Your Offers
              </CardTitle>
            </CardHeader>
            <CardContent>
              {dashboard?.active_offers?.length > 0 || dashboard?.accepted_offers?.length > 0 ? (
                <div className="space-y-3">
                  {dashboard.accepted_offers?.map((offer) => (
                    <div
                      key={offer.offer_id}
                      className="p-3 bg-green-50 rounded-lg border border-green-200"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium">{offer.property_address}</p>
                          <p className="text-lg font-bold text-green-700">
                            £{offer.offer_amount?.toLocaleString()}
                          </p>
                        </div>
                        <Badge className="bg-green-600">Accepted!</Badge>
                      </div>
                    </div>
                  ))}
                  {dashboard.active_offers?.map((offer) => (
                    <div
                      key={offer.offer_id}
                      className="p-3 bg-slate-50 rounded-lg"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium">{offer.property_address}</p>
                          <p className="text-lg font-bold text-slate-700">
                            £{offer.offer_amount?.toLocaleString()}
                          </p>
                          <p className="text-xs text-slate-500">
                            Submitted {new Date(offer.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <Badge variant="secondary">{offer.status}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <PoundSterling className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No active offers</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-3 gap-4">
              <Button
                variant="outline"
                className="h-20 flex-col"
                onClick={() => navigate("/buyer/properties")}
              >
                <Search className="w-6 h-6 mb-2" />
                Search Properties
              </Button>
              <Button
                variant="outline"
                className="h-20 flex-col"
                onClick={() => navigate("/buyer/viewings")}
              >
                <Calendar className="w-6 h-6 mb-2" />
                My Viewings
              </Button>
              <Button
                variant="outline"
                className="h-20 flex-col"
                onClick={() => navigate("/buyer/profile")}
              >
                <User className="w-6 h-6 mb-2" />
                My Profile
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Feedback Modal */}
      <Dialog open={feedbackModal} onOpenChange={setFeedbackModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>How was your viewing?</DialogTitle>
            <DialogDescription>
              {selectedViewing?.property_address}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-3 gap-2">
              <Button
                variant={feedbackAction === "offer" ? "default" : "outline"}
                className="h-20 flex-col"
                onClick={() => setFeedbackAction("offer")}
              >
                <PoundSterling className="w-6 h-6 mb-1" />
                <span className="text-xs">Make Offer</span>
              </Button>
              <Button
                variant={feedbackAction === "second_viewing" ? "default" : "outline"}
                className="h-20 flex-col"
                onClick={() => setFeedbackAction("second_viewing")}
              >
                <Eye className="w-6 h-6 mb-1" />
                <span className="text-xs">Second Viewing</span>
              </Button>
              <Button
                variant={feedbackAction === "not_interested" ? "default" : "outline"}
                className="h-20 flex-col"
                onClick={() => setFeedbackAction("not_interested")}
              >
                <MessageSquare className="w-6 h-6 mb-1" />
                <span className="text-xs">Not Interested</span>
              </Button>
            </div>
            
            {feedbackAction === "offer" && (
              <div>
                <Label>Offer Amount (£)</Label>
                <Input
                  type="number"
                  placeholder="e.g. 350000"
                  value={offerAmount}
                  onChange={(e) => setOfferAmount(e.target.value)}
                />
              </div>
            )}
            
            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                placeholder="Any comments about the property..."
                value={feedbackNotes}
                onChange={(e) => setFeedbackNotes(e.target.value)}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setFeedbackModal(false)}>
              Cancel
            </Button>
            <Button onClick={submitFeedback} disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BuyerDashboard;
